import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LogoPreview } from './LogoPreview';

const BOX_SIZE = 260;
const MIN_ZOOM = 1; // en dessous de 1, l'image entière tient déjà dans la boîte : "dézoomer" ne recadre plus rien.
const MAX_ZOOM = 5;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export interface LogoCrop {
  cropX: number;
  cropY: number;
  cropWidth: number;
  cropHeight: number;
  zoom: number;
}

export function LogoEditorModal({
  svgContent,
  initial,
  companyName,
  onCompanyNameChange,
  onCancel,
  onSave,
}: {
  svgContent: string;
  initial: LogoCrop;
  companyName?: string;
  onCompanyNameChange?: (value: string) => void;
  onCancel: () => void;
  onSave: (crop: LogoCrop) => void;
}) {
  const { t } = useTranslation();
  const initialZoom = clamp(initial.zoom, MIN_ZOOM, MAX_ZOOM);
  const [cropX, setCropX] = useState(clamp(initial.cropX, 0, 1 - 1 / initialZoom));
  const [cropY, setCropY] = useState(clamp(initial.cropY, 0, 1 - 1 / initialZoom));
  const [zoom, setZoom] = useState(initialZoom);
  const dragState = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null);

  // La fenêtre de recadrage a une taille de 1/zoom (fraction de l'image) : cropX/cropY ne
  // peuvent jamais dépasser [0, 1 - 1/zoom] sans quoi la fenêtre sortirait de l'image.
  const maxOffset = 1 - 1 / zoom;

  const handlePointerDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture(e.pointerId);
    dragState.current = { startX: e.clientX, startY: e.clientY, originX: cropX, originY: cropY };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragState.current) return;
    const dx = (e.clientX - dragState.current.startX) / BOX_SIZE;
    const dy = (e.clientY - dragState.current.startY) / BOX_SIZE;
    // Glisser vers la droite doit déplacer l'image vers la droite (cropX diminue).
    setCropX(clamp(dragState.current.originX - dx, 0, maxOffset));
    setCropY(clamp(dragState.current.originY - dy, 0, maxOffset));
  };

  const handlePointerUp = () => {
    dragState.current = null;
  };

  const handleZoomChange = (value: number) => {
    const nextZoom = clamp(value, MIN_ZOOM, MAX_ZOOM);
    const nextMaxOffset = 1 - 1 / nextZoom;
    setZoom(nextZoom);
    setCropX((x) => clamp(x, 0, nextMaxOffset));
    setCropY((y) => clamp(y, 0, nextMaxOffset));
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal card" style={{ width: BOX_SIZE + 40 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('logos.editTitle')}</h3>
          <button className="btn" onClick={onCancel}>
            {t('common.close')}
          </button>
        </div>

        {onCompanyNameChange && (
          <div className="form-field">
            <label>{t('logos.companyName')}</label>
            <input value={companyName ?? ''} onChange={(e) => onCompanyNameChange(e.target.value)} required autoFocus />
          </div>
        )}

        <div
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          style={{ cursor: 'grab', touchAction: 'none' }}
        >
          <LogoPreview svgContent={svgContent} cropX={cropX} cropY={cropY} zoom={zoom} size={BOX_SIZE} />
        </div>

        <div className="form-field" style={{ marginTop: 12 }}>
          <label>{t('logos.zoom')}</label>
          <input
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.05}
            value={zoom}
            onChange={(e) => handleZoomChange(parseFloat(e.target.value))}
          />
        </div>
        <p style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{t('logos.dragHint')}</p>

        <div className="toolbar">
          <button
            className="btn btn-primary"
            disabled={onCompanyNameChange ? !companyName?.trim() : false}
            onClick={() => onSave({ cropX, cropY, cropWidth: 1 / zoom, cropHeight: 1 / zoom, zoom })}
          >
            {t('common.confirm')}
          </button>
          <button className="btn" onClick={onCancel}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
